import { useEffect, useState } from "react";
import { ShieldCheck, Download, DatabaseBackup, RefreshCw, History } from "lucide-react";
import { RestoreDialog } from "./RestoreDialog";
import { DriveOffsiteCard } from "./DriveOffsiteCard";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const kb = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function Guardrail({ g }) {
  if (!g) return <p className="text-xs text-stone-500" data-testid="guardrail-none">No comparison yet — save a first archive.</p>;
  if (g.ok) return <p className="text-xs text-emerald-800" data-testid="guardrail-ok">No collection shrank since the last archive · checked {when(g.at)} ({g.trigger}).</p>;
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900" data-testid="guardrail-alert">
      <div className="font-semibold">Fewer records than the archive saved {when(g.compared_to)}</div>
      <ul className="mt-1 space-y-0.5">{g.shrank.map((s) => <li key={s.collection} data-testid={`guardrail-shrank-${s.collection}`}><strong>{s.collection}</strong>: {s.before.toLocaleString()} → {s.after.toLocaleString()}</li>)}</ul>
      <div className="mt-1 text-rose-800/80">Nothing was changed automatically. Download the archive below if you need it.</div>
    </div>
  );
}

function Checklist({ st }) {
  const m = st.migration;
  const fresh = st.last && (Date.now() - new Date(st.last.created_at)) < 36 * 3600e3;
  const Row = ({ ok, id, children }) => <li className="flex items-start gap-2" data-testid={id}><span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${ok ? "bg-emerald-600" : "bg-amber-500"}`} /><span>{children}</span></li>;
  return (
    <ul className="text-xs text-stone-700 space-y-1.5" data-testid="publish-checklist">
      <Row ok={fresh} id="check-backup-age">Last archive: <strong>{st.last_age}</strong>{st.last ? ` · ${st.last.total.toLocaleString()} records · ${kb(st.last.size)}` : ""}</Row>
      <Row ok id="check-migration">Coach migration dry-run: would create <strong>{m.would_create}</strong> connection{m.would_create === 1 ? "" : "s"}, {m.already_connected} already in place · {m.deletes} deletes · {m.rewrites} rewrites</Row>
      <Row ok={st.guardrail?.ok !== false} id="check-guardrail">Record-count guardrail: {st.guardrail ? (st.guardrail.ok ? "clear" : `${st.guardrail.shrank.length} collection(s) shrank`) : "not run yet"}</Row>
      <Row ok id="check-env">This environment holds <strong>{Object.values(st.counts_now).reduce((a, b) => a + b, 0).toLocaleString()}</strong> records. The live site has its own database — compare after publishing.</Row>
    </ul>
  );
}

/** Beta board: Data safety — Back up now, nightly archives with retention, record-count guardrail and a pre-publish checklist. */
export function DataSafetyCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const load = () => api.get("/admin/data-safety").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const backup = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/backups"); toast.success(`Saved ${r.data.run.total.toLocaleString()} records (${kb(r.data.run.size)}).`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const download = async (run) => {
    try {
      const r = await api.get(`/admin/backups/${run.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = run.filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!st) return null;
  const rows = showAll ? st.backups : st.backups.slice(0, 5);
  return (
    <section className="warm-card p-6 space-y-4" data-testid="data-safety-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-emerald-700" /></div>
          <div>
            <div className="font-display font-bold text-stone-900">Data safety</div>
            <div className="text-sm text-stone-600 mt-0.5" data-testid="data-safety-summary">
              {st.last ? `Last saved ${when(st.last.created_at)} · ${st.last.total.toLocaleString()} records · ${kb(st.last.size)}` : "No archive saved yet."}
            </div>
            <p className="text-xs text-stone-500 mt-1">Every collection, encrypted with the field key, saved nightly at {String(st.retention.nightly_hour_utc).padStart(2, "0")}:00 UTC. Keeps {st.retention.daily} daily and {st.retention.monthly} monthly archives.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={busy} onClick={backup} data-testid="backup-now-btn" className="amber-btn text-xs py-1.5 inline-flex items-center gap-1"><DatabaseBackup className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Back up now"}</button>
          <button disabled={busy} onClick={() => api.post("/admin/guardrail/check").then(load)} data-testid="guardrail-check-btn" className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 text-stone-700"><RefreshCw className="w-3.5 h-3.5" /> Re-check counts</button>
        </div>
      </div>
      <Guardrail g={st.guardrail} />
      {st.last_restore && <p className="text-xs text-stone-500" data-testid="last-restore">Last restore {when(st.last_restore.at)} · {st.last_restore.mode === "member" ? "one member" : "everything"} from the {when(st.last_restore.archive_at)} archive · {st.last_restore.totals.add} re-added, {st.last_restore.totals.change} updated, 0 deleted.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">Before you publish</div>
          <Checklist st={st} />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">Archives</div>
          {!rows.length && <p className="text-xs text-stone-500" data-testid="backups-empty">None yet.</p>}
          <ul className="space-y-1.5" data-testid="backups-list">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 text-xs text-stone-700 rounded-lg bg-stone-50 px-3 py-1.5" data-testid={`backup-row-${r.id}`}>
                <span>{when(r.created_at)} · {r.total.toLocaleString()} records · {kb(r.size)} <span className="text-stone-400">· {r.kind}{r.monthly ? " · monthly keeper" : ""}</span></span>
                <span className="flex gap-3 shrink-0">
                  <button onClick={() => download(r)} data-testid={`backup-download-${r.id}`} className="inline-flex items-center gap-1 font-semibold text-stone-800 hover:text-amber-800"><Download className="w-3.5 h-3.5" /> Download</button>
                  <button onClick={() => setRestoring(r)} data-testid={`backup-restore-${r.id}`} className="inline-flex items-center gap-1 font-semibold text-stone-800 hover:text-emerald-800"><History className="w-3.5 h-3.5" /> Restore</button>
                </span>
              </li>
            ))}
          </ul>
          {st.backups.length > 5 && <button onClick={() => setShowAll(!showAll)} data-testid="backups-toggle" className="text-xs text-stone-600 underline mt-2">{showAll ? "Show fewer" : `Show all ${st.backups.length}`}</button>}
          {!!st.last && <details className="mt-3 text-xs text-stone-600"><summary className="cursor-pointer">Records per collection in the last archive</summary>
            <ul className="grid grid-cols-2 gap-x-4 mt-1 font-mono text-[11px]" data-testid="backup-counts">{Object.entries(st.last.counts).map(([c, n]) => <li key={c}>{c}: {n.toLocaleString()}</li>)}</ul></details>}
        </div>
      </div>
      <DriveOffsiteCard lastRun={st.last} />
      {restoring && <RestoreDialog run={restoring} onClose={() => setRestoring(null)} onDone={load} />}
    </section>
  );
}
